import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAvailableApps } from '@/hooks/useAvailableApps';
import { useInstalledApps } from '@/hooks/useInstalledApps';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Download, CheckCircle2, AlertCircle } from 'lucide-react';
import { showSKOverlay } from '@/utils/nativeInterface';
import { deduplicateApps } from '@/utils/appUtils';

// Version comparison utility
const compareVersions = (installedVersion: string, availableVersion: string): 'update' | 'current' | 'newer' => {
  if (!installedVersion || installedVersion === 'Not installed') return 'update';
  if (installedVersion === availableVersion) return 'current';
  
  // Simple version comparison - you might want to use a library like semver for complex cases
  const parseVersion = (version: string) => {
    const cleaned = version.replace(/[^\d.]/g, ''); // Remove non-numeric chars except dots
    return cleaned.split('.').map(num => parseInt(num) || 0);
  };
  
  const installed = parseVersion(installedVersion);
  const available = parseVersion(availableVersion);
  
  // Compare version parts
  for (let i = 0; i < Math.max(installed.length, available.length); i++) {
    const installedPart = installed[i] || 0;
    const availablePart = available[i] || 0;
    
    if (availablePart > installedPart) return 'update';
    if (installedPart > availablePart) return 'newer';
  }
  
  return 'current';
};

const Updates: React.FC = () => {
  const navigate = useNavigate();
  const { data: availableAppsData, isLoading: isLoadingAvailableApps } = useAvailableApps();
  const { installedApps, isLoading: isLoadingInstalledApps } = useInstalledApps();
  
  // Flatten all available apps data and deduplicate
  const allAvailableApps = useMemo(() => {
    const apps = availableAppsData?.pages.flatMap(page => page.apps) || [];
    return deduplicateApps(apps);
  }, [availableAppsData]);

  // Find apps that need updates
  const appsNeedingUpdates = useMemo(() => {
    if (!allAvailableApps.length || !installedApps) return [];
    
    console.log('Updates - Available apps:', allAvailableApps.length);
    console.log('Updates - Installed apps:', installedApps.length);
    
    const updateCandidates = allAvailableApps
      .map(app => {
        // Find the corresponding installed app
        const installedApp = installedApps.find(installed => installed.trackId === app.appStoreId);
        
        if (!installedApp) {
          // App is not installed, don't show in updates
          return null;
        }

        const installedVersion = installedApp.version || 'Unknown';
        const availableVersion = app.version;
        const comparison = compareVersions(installedVersion, availableVersion);
        
        console.log(`Updates - ${app.name}: ${installedVersion} vs ${availableVersion} = ${comparison}`);
        
        // Only show apps that need updates
        if (comparison === 'update') {
          return {
            ...app,
            installedVersion,
            availableVersion,
            updateStatus: comparison
          };
        }
        
        return null;
      })
      .filter(app => app !== null);

    console.log('Updates - Apps needing updates:', updateCandidates.length);
    return updateCandidates;
  }, [allAvailableApps, installedApps]);

  const handleUpdate = async (appStoreId: string) => {
    try {
      await showSKOverlay(appStoreId);
    } catch (error) {
      console.error('Error showing SKOverlay:', error);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  const isLoading = isLoadingAvailableApps || isLoadingInstalledApps;

  if (isLoading) {
    return (
      <div className="h-screen bg-background flex flex-col">
        {/* Fixed Header */}
        <div className="fixed top-0 left-0 right-0 bg-background/80 backdrop-blur-md z-50 flex-shrink-0">
          <div className="p-4">
            <div className="flex items-center space-x-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                className="p-2"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="text-xl font-bold text-foreground">Updates</h1>
            </div>
          </div>
        </div>

        {/* Content with top padding */}
        <div className="pt-20 flex-1 overflow-auto">
          <div className="border-b border-border">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse border-b border-border">
                <div className="p-3 flex items-center space-x-3">
                  <div className="w-12 h-12 bg-muted rounded-lg"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-muted rounded w-3/4 mb-1"></div>
                    <div className="h-3 bg-muted rounded w-1/2"></div>
                  </div>
                  <div className="w-20 h-8 bg-muted rounded"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-background flex flex-col">
      {/* Fixed Header */}
      <div className="fixed top-0 left-0 right-0 bg-background/80 backdrop-blur-md z-50 flex-shrink-0">
        <div className="p-4">
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              className="p-2"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold text-foreground">Updates</h1>
            {appsNeedingUpdates.length > 0 && (
              <div className="bg-destructive text-destructive-foreground text-xs px-2 py-1 rounded-full font-medium">
                {appsNeedingUpdates.length}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Scrollable Content with top padding and bottom padding for bottom nav */}
      <div className="pt-20 pb-16 flex-1 overflow-auto bg-background">
        {appsNeedingUpdates.length > 0 ? (
          <div className="space-y-0">
            {appsNeedingUpdates.map((app) => (
              <div key={app.id} className="border-b border-border">
                <div className="p-4 flex items-center space-x-3">
                  <div className="w-12 h-12 bg-muted rounded-lg overflow-hidden flex-shrink-0">
                    <img 
                      src={app.icon} 
                      alt={app.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = '/placeholder.svg';
                      }}
                    />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-foreground truncate">
                      {app.name}
                    </h3>
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                      <span>v{app.installedVersion}</span>
                      <ArrowLeft className="w-3 h-3 rotate-180" />
                      <span className="text-primary font-medium">v{app.availableVersion}</span>
                    </div>
                    {app.category && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {app.category}
                      </p>
                    )}
                  </div>
                  
                  <Button
                    size="sm"
                    onClick={() => handleUpdate(app.appStoreId)}
                    className="flex-shrink-0"
                    disabled={!app.appStoreId || app.appStoreId.trim() === ''}
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Update
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-4">
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 bg-muted rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-lg font-medium text-foreground mb-2">
                All apps are up to date
              </h2>
              <p className="text-muted-foreground">
                No apps need to be updated at this time.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Updates;
